/**
 * Baremetal Instances Page
 * Displays user's provisioned GPU instances with SSH details
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CpuChipIcon,
  ServerStackIcon,
  CheckCircleIcon,
  CommandLineIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  XMarkIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import Header from '../../components/layout/Header';
import { marketplaceApi } from '../../services/marketplaceApi';
import { ReservationResponse } from '../../types/marketplace';

const Instances: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedInstance, setSelectedInstance] = useState<ReservationResponse | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Fetch all reservations and filter for provisioned ones
  const { data: reservationsData, isLoading, isError, refetch } = useQuery({
    queryKey: ['my-instances'],
    queryFn: () => marketplaceApi.listMyReservations({ pageSize: 100 }),
  });

  // Cancel/delete reservation mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => marketplaceApi.cancelReservation(id),
    onSuccess: () => {
      toast.success('Instance deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['my-instances'] });
      setShowDeleteModal(false);
      setSelectedInstance(null);
    },
    onError: () => {
      toast.error('Failed to delete instance');
    },
  });

  // Filter for provisioned instances only
  const instances = (reservationsData?.data || []).filter(
    (r: ReservationResponse) =>
      r.provisioning.status === 'completed' &&
      r.payment.status === 'paid' &&
      r.status === 'active'
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleViewDetails = (instance: ReservationResponse) => {
    setSelectedInstance(instance);
    setShowDetailsModal(true);
  };

  const handleDeleteClick = (instance: ReservationResponse) => {
    setSelectedInstance(instance);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (selectedInstance) {
      deleteMutation.mutate(selectedInstance.id);
    }
  };

  return (
    <div>
      <Header
        title="My Instances"
        subtitle="View and manage your provisioned baremetal GPU instances"
      />

      <div className="p-6 space-y-6">
        {/* Instances Grid */}
        {isLoading ? (
          <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-700 p-12">
            <div className="flex flex-col items-center justify-center">
              <div className="w-8 h-8 border-4 border-gray-700 border-t-primary-500 rounded-full animate-spin mb-4"></div>
              <p className="text-sm text-gray-400">Loading instances...</p>
            </div>
          </div>
        ) : isError ? (
          <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-700 p-12">
            <div className="text-center">
              <p className="text-red-400 mb-4">Failed to load instances</p>
              <button onClick={() => refetch()} className="btn-primary">
                Retry
              </button>
            </div>
          </div>
        ) : instances.length === 0 ? (
          <div className="bg-gray-900 rounded-xl shadow-sm border border-gray-700 p-12">
            <div className="text-center">
              <ServerStackIcon className="mx-auto h-12 w-12 text-gray-600" />
              <h3 className="mt-2 text-sm font-medium text-white">No instances</h3>
              <p className="mt-1 text-sm text-gray-400">
                You don't have any provisioned instances yet.
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Once you purchase a GPU and the admin provisions your machine, it will appear here.
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {instances.map((instance: ReservationResponse) => (
              <div
                key={instance.id}
                onClick={() => handleViewDetails(instance)}
                className="bg-gray-900 rounded-xl shadow-sm border border-gray-700 p-6 hover:shadow-md hover:border-primary-500 transition-all cursor-pointer"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-500/20 rounded-lg">
                      <ServerStackIcon className="w-6 h-6 text-green-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{instance.code}</h3>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                        <CheckCircleIcon className="w-3 h-3" />
                        Running
                      </span>
                    </div>
                  </div>
                </div>

                {/* GPU Info */}
                <div className="flex items-center gap-2 mb-3">
                  <CpuChipIcon className="w-5 h-5 text-gray-600" />
                  <div>
                    <p className="font-medium text-white">{instance.gpu.name}</p>
                    <p className="text-xs text-gray-400">{instance.gpu.vramGB} GB VRAM</p>
                  </div>
                </div>

                {/* SSH Access */}
                {instance.ssh && (
                  <div className="bg-gray-800 rounded-lg p-3 mb-3">
                    <p className="text-xs font-medium text-gray-400 mb-1">SSH Access</p>
                    <div className="flex items-center justify-between">
                      <code className="text-sm text-gray-300 font-mono">
                        {instance.ssh.username}@{instance.ssh.host}
                      </code>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(
                            `ssh ${instance.ssh!.username}@${instance.ssh!.host} -p ${instance.ssh!.port}`
                          );
                        }}
                        className="p-1 text-gray-600 hover:text-gray-400"
                        title="Copy SSH command"
                      >
                        <DocumentDuplicateIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Configuration Summary */}
                <div className="text-xs text-gray-400 space-y-1">
                  <p>
                    {instance.configuration.gpuCount}x GPU | {instance.configuration.cpuCores} vCPU |{' '}
                    {instance.configuration.ramGB} GB RAM
                  </p>
                  <p>{instance.configuration.storageGB} GB {instance.configuration.storageType}</p>
                </div>

                {/* Timing */}
                <div className="mt-3 pt-3 border-t border-gray-700 flex items-center gap-1 text-xs text-gray-500">
                  <ClockIcon className="w-3 h-3" />
                  <span>Expires: {formatDate(instance.timing.endTime)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Instance Details Modal */}
      {showDetailsModal && selectedInstance && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-900 border border-gray-800 rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <div>
                <h3 className="text-lg font-semibold text-white">
                  Instance Details
                </h3>
                <p className="text-sm text-gray-400">{selectedInstance.code}</p>
              </div>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedInstance(null);
                }}
                className="p-2 text-gray-400 hover:text-gray-300 rounded-lg hover:bg-gray-700"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Status */}
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-green-500/20 text-green-400">
                  <CheckCircleIcon className="w-4 h-4" />
                  Running
                </span>
                <span className="text-sm text-gray-400">
                  Provisioned on {formatDate(selectedInstance.provisioning.provisionedAt || selectedInstance.createdAt)}
                </span>
              </div>

              {/* SSH Access Section */}
              {selectedInstance.ssh && (
                <div className="bg-gray-800 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <CommandLineIcon className="w-4 h-4" />
                    SSH Access
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">
                        SSH Command
                      </label>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-sm font-mono text-gray-300 overflow-x-auto">
                          ssh {selectedInstance.ssh.username}@{selectedInstance.ssh.host} -p{' '}
                          {selectedInstance.ssh.port}
                        </code>
                        <button
                          onClick={() =>
                            copyToClipboard(
                              `ssh ${selectedInstance.ssh!.username}@${selectedInstance.ssh!.host} -p ${selectedInstance.ssh!.port}`
                            )
                          }
                          className="p-2 text-gray-400 hover:text-gray-300 bg-gray-900 border border-gray-700 rounded-lg"
                        >
                          <DocumentDuplicateIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Host</label>
                        <p className="text-sm text-white font-mono">{selectedInstance.ssh.host}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Port</label>
                        <p className="text-sm text-white font-mono">{selectedInstance.ssh.port}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Username</label>
                        <p className="text-sm text-white font-mono">{selectedInstance.ssh.username}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* GPU Details */}
              <div>
                <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <CpuChipIcon className="w-4 h-4" />
                  GPU Details
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">GPU Model</label>
                    <p className="text-sm text-white">{selectedInstance.gpu.name}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">VRAM</label>
                    <p className="text-sm text-white">{selectedInstance.gpu.vramGB} GB</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">Type</label>
                    <p className="text-sm text-white">{selectedInstance.gpu.type || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">Manufacturer</label>
                    <p className="text-sm text-white">{selectedInstance.gpu.manufacturer || 'NVIDIA'}</p>
                  </div>
                </div>
              </div>

              {/* Machine Configuration */}
              <div>
                <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <ServerStackIcon className="w-4 h-4" />
                  Machine Configuration
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">GPU Count</label>
                    <p className="text-sm text-white">{selectedInstance.configuration.gpuCount}x</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">vCPU</label>
                    <p className="text-sm text-white">{selectedInstance.configuration.cpuCores} cores</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">RAM</label>
                    <p className="text-sm text-white">{selectedInstance.configuration.ramGB} GB</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">Storage</label>
                    <p className="text-sm text-white">
                      {selectedInstance.configuration.storageGB} GB {selectedInstance.configuration.storageType}
                    </p>
                  </div>
                  {selectedInstance.configuration.networkGbps && (
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">Network</label>
                      <p className="text-sm text-white">{selectedInstance.configuration.networkGbps} Gbps</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Timing & Pricing */}
              <div>
                <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <ClockIcon className="w-4 h-4" />
                  Reservation Details
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">Start Time</label>
                    <p className="text-sm text-white">{formatDate(selectedInstance.timing.startTime)}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">End Time</label>
                    <p className="text-sm text-white">{formatDate(selectedInstance.timing.endTime)}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">Duration</label>
                    <p className="text-sm text-white">{selectedInstance.timing.durationHours} hours</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1">Amount Paid</label>
                    <p className="text-sm text-white">
                      ₹{(selectedInstance.payment.amount || selectedInstance.pricing.estimatedCost).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-700 bg-gray-800">
              <button
                onClick={() => handleDeleteClick(selectedInstance)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-400 bg-red-500/20 rounded-lg hover:bg-red-500/30 transition-colors"
              >
                <TrashIcon className="w-4 h-4" />
                Delete Instance
              </button>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedInstance(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-900 border border-gray-600 rounded-lg hover:bg-gray-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedInstance && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-900 border border-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-red-500/20 rounded-full">
                <ExclamationTriangleIcon className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Delete Instance</h3>
                <p className="text-sm text-gray-400">This action cannot be undone</p>
              </div>
            </div>

            <p className="text-sm text-gray-400 mb-4">
              Are you sure you want to delete the instance <strong className="text-white">{selectedInstance.code}</strong>?
              This will terminate the machine and you will lose access to it.
            </p>

            <div className="bg-gray-800 rounded-lg p-3 mb-6">
              <p className="text-xs text-gray-400">Instance Details:</p>
              <p className="text-sm font-medium text-white">{selectedInstance.gpu.name}</p>
              {selectedInstance.ssh && (
                <p className="text-xs text-gray-400">
                  {selectedInstance.ssh.username}@{selectedInstance.ssh.host}
                </p>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteMutation.isPending}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete Instance'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Instances;
